import React, {useCallback, useMemo, useState} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import HubIcon from '@mui/icons-material/Hub';
import {MinimalCutSet} from '../../../../utils/minimal-cut-sets';
import {FaultTreeNode} from '../../../../@types/faultTreeNode';
import Stack from '@mui/material/Stack';

interface FMEATableProps {
    cutSets: MinimalCutSet[];
    nodeMap: Map<number, FaultTreeNode>;
    nodeNameMap: Map<number, string>;
    totalProbability: number;
    structuralHubIds?: Set<number>;
}

interface FMEARow {
    id: number;
    name: string;
    isBarrier: boolean;
    isHub: boolean;
    isSPF: boolean;
    probability: number;
    cutSetCount: number;
    raw: number;
    rrw: number;
    severity: number;
    occurrence: number;
    detection: number;
    rpn: number;
}

type SortField = 'name' | 'rpn' | 's' | 'o' | 'd' | 'cutSetCount';
type SortDir = 'asc' | 'desc';

// ---------------------------------------------------------------------------
// Mapping functions: continuous values → 1–10 FMEA scale
// ---------------------------------------------------------------------------

/**
 * Occurrence rating from probability.
 * Maps log10(probability) linearly onto 1–10.
 * P=1e-7 or lower → 1, P=1e-1 or higher → 10.
 */
function deriveOccurrence(probability: number): number {
    if (probability <= 0) return 1;
    const log = Math.log10(probability);
    const rating = Math.round(((log + 7) / 6) * 9 + 1);
    return Math.max(1, Math.min(10, rating));
}

/**
 * Severity rating from RAW (Risk Achievement Worth).
 * RAW=1 means no impact → 1. RAW≥1000 → 10.
 * Uses log10(RAW) to spread the scale.
 */
function deriveSeverity(raw: number): number {
    if (!isFinite(raw) || raw <= 0) return 10;
    if (raw <= 1) return 1;
    const log = Math.log10(raw);
    const rating = Math.round((log / 3) * 9 + 1);
    return Math.max(1, Math.min(10, rating));
}

/**
 * Detection rating from RRW (Risk Reduction Worth).
 * High RRW = easy to mitigate = low detection difficulty = low D.
 * RRW≥10 → D=1 (easy to detect/mitigate). RRW≈1 → D=10 (hard).
 * Inverted log scale.
 */
function deriveDetection(rrw: number): number {
    if (!isFinite(rrw)) return 1;
    if (rrw <= 1) return 10;
    const log = Math.log10(rrw);
    const rating = Math.round(10 - (log / 2) * 9);
    return Math.max(1, Math.min(10, rating));
}

/** RPN color based on value (max 1000). */
function rpnColor(rpn: number): string {
    if (rpn >= 200) return 'rgba(211, 47, 47, 0.85)';
    if (rpn >= 100) return 'rgba(211, 47, 47, 0.5)';
    if (rpn >= 50) return 'rgba(255, 152, 0, 0.4)';
    return 'transparent';
}

function rpnTextColor(rpn: number): string {
    return rpn >= 200 ? '#fff' : '#333';
}

/** Color for individual S/O/D rating cells. */
function ratingColor(val: number): string {
    if (val >= 8) return 'rgba(211, 47, 47, 0.6)';
    if (val >= 5) return 'rgba(255, 152, 0, 0.3)';
    if (val >= 3) return 'rgba(255, 235, 59, 0.25)';
    return 'transparent';
}

const FMEATable: React.FC<FMEATableProps> = ({
    cutSets, nodeMap, nodeNameMap, totalProbability, structuralHubIds,
}) => {
    const [sortField, setSortField] = useState<SortField>('rpn');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [searchText, setSearchText] = useState<string>('');

    const rows = useMemo((): FMEARow[] => {
        if (totalProbability === 0) return [];

        const allIds = new Set<number>();
        cutSets.forEach((cs) => cs.baseEventIds.forEach((id) => allIds.add(id)));

        return Array.from(allIds).map((id) => {
            const node = nodeMap.get(id);
            const prob = node?.probability ?? 0;

            const containingCS = cutSets.filter((cs) => cs.baseEventIds.has(id));
            const cutSetCount = containingCS.length;

            // RAW
            let rawTotal = 0;
            for (const cs of cutSets) {
                if (cs.baseEventIds.has(id)) {
                    let p = 1;
                    for (const eid of cs.baseEventIds) {
                        if (eid !== id) {
                            p *= nodeMap.get(eid)?.probability ?? 0;
                        }
                    }
                    rawTotal += p;
                } else {
                    rawTotal += cs.probability;
                }
            }
            const raw = rawTotal / totalProbability;

            // RRW
            const nonContainingTotal = cutSets
                .filter((cs) => !cs.baseEventIds.has(id))
                .reduce((sum, cs) => sum + cs.probability, 0);
            const rrw = nonContainingTotal > 0 ? totalProbability / nonContainingTotal : Infinity;

            const severity = deriveSeverity(raw);
            const occurrence = deriveOccurrence(prob);
            const detection = deriveDetection(rrw);

            return {
                id,
                name: nodeNameMap.get(id) || `ID:${id}`,
                isBarrier: node?.nodeClass === 'BARRIER',
                isHub: structuralHubIds?.has(id) ?? false,
                isSPF: containingCS.some((cs) => cs.baseEventIds.size === 1),
                probability: prob,
                cutSetCount,
                raw,
                rrw,
                severity,
                occurrence,
                detection,
                rpn: severity * occurrence * detection,
            };
        });
    }, [cutSets, nodeMap, nodeNameMap, totalProbability, structuralHubIds]);

    const sortedRows = useMemo((): FMEARow[] => {
        const dir = sortDir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            switch (sortField) {
                case 'name': return a.name.localeCompare(b.name) * dir;
                case 'rpn': return (a.rpn - b.rpn) * dir;
                case 's': return (a.severity - b.severity) * dir;
                case 'o': return (a.occurrence - b.occurrence) * dir;
                case 'd': return (a.detection - b.detection) * dir;
                case 'cutSetCount': return (a.cutSetCount - b.cutSetCount) * dir;
                default: return 0;
            }
        });
    }, [rows, sortField, sortDir]);

    const filteredRows = useMemo((): FMEARow[] => {
        if (!searchText.trim()) return sortedRows;
        const term = searchText.trim().toLowerCase();
        return sortedRows.filter((row) => row.name.toLowerCase().includes(term));
    }, [sortedRows, searchText]);

    const handleSort = (field: SortField): void => {
        if (sortField === field) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir(field === 'name' ? 'asc' : 'desc');
        }
    };

    const handleExportCSV = useCallback((): void => {
        const header = ['Basic Event', 'Failure Paths', 'Severity', 'Occurrence', 'Detection', 'RPN', 'Probability', 'RIF', 'RRF'].join(',');
        const csvRows = filteredRows.map((row) =>
            [
                `"${row.name.replace(/"/g, '""')}"`,
                row.cutSetCount,
                row.severity,
                row.occurrence,
                row.detection,
                row.rpn,
                row.probability === 0 ? '0' : row.probability.toExponential(2),
                isFinite(row.raw) ? row.raw.toFixed(1) : 'Inf',
                isFinite(row.rrw) ? row.rrw.toFixed(1) : 'Inf',
            ].join(',')
        );
        const csv = [header, ...csvRows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `criticality_analysis_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [filteredRows]);

    if (totalProbability === 0) {
        return (
            <Box sx={{ px: 2, py: 2, borderBottom: 1, borderColor: 'divider', textAlign: 'center' }}>
                <Typography variant="body2" color="error">
                    Criticality Analysis requires non-zero base event probabilities.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1 }}>
                <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>Criticality Analysis</Typography>
                <Tooltip
                    title="What happens if a basic event occurs?."
                    arrow
                >
                    <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help', flexShrink: 0 }}>
                        (?)
                    </Typography>
                </Tooltip>
                <TextField
                    size="small"
                    placeholder="Search events"
                    value={searchText}
                    onChange={(e): void => setSearchText(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{ minWidth: 150, ml: 'auto' }}
                />
                <Tooltip title="Export to CSV">
                    <IconButton size="small" onClick={handleExportCSV}>
                        <FileDownloadIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, display: 'block', pb: 0.5 }}>
                Severity, Occurrence, and Detection are derived from importance measures.
            </Typography>
            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400, overflow: 'auto' }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'name'}
                                    direction={sortField === 'name' ? sortDir : 'asc'}
                                    onClick={(): void => handleSort('name')}
                                >
                                    Basic Event
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === 'cutSetCount'}
                                    direction={sortField === 'cutSetCount' ? sortDir : 'desc'}
                                    onClick={(): void => handleSort('cutSetCount')}
                                >
                                    <Tooltip title="Number of cut sets (failure paths) containing this event" arrow>
                                        <span>Failure Paths</span>
                                    </Tooltip>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center" sx={{ minWidth: 52 }}>
                                <TableSortLabel
                                    active={sortField === 's'}
                                    direction={sortField === 's' ? sortDir : 'desc'}
                                    onClick={(): void => handleSort('s')}
                                >
                                    <Tooltip title="Severity: derived from RIF (Risk Increase Factor). How much worse does the system get if this event is guaranteed to occur." arrow>
                                        <span>Severity</span>
                                    </Tooltip>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center" sx={{ minWidth: 52 }}>
                                <TableSortLabel
                                    active={sortField === 'o'}
                                    direction={sortField === 'o' ? sortDir : 'desc'}
                                    onClick={(): void => handleSort('o')}
                                >
                                    <Tooltip title="Occurrence: derived from base event probability. Higher probability = higher rating." arrow>
                                        <span>Occurrence</span>
                                    </Tooltip>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center" sx={{ minWidth: 52 }}>
                                <TableSortLabel
                                    active={sortField === 'd'}
                                    direction={sortField === 'd' ? sortDir : 'desc'}
                                    onClick={(): void => handleSort('d')}
                                >
                                    <Tooltip title="Detection: derived from RRF (Risk Reduction Factor) inverted. High RRF = easy to mitigate = low D. Low RRF = hard to mitigate = high D." arrow>
                                        <span>Detection</span>
                                    </Tooltip>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === 'rpn'}
                                    direction={sortField === 'rpn' ? sortDir : 'desc'}
                                    onClick={(): void => handleSort('rpn')}
                                >
                                    <Tooltip title="Risk Priority Number = Severity x Occurrence x Detection. Higher = more critical to address." arrow>
                                        <span>Risk Priority Number</span>
                                    </Tooltip>
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRows.map((row) => (
                            <TableRow key={row.id} hover>
                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        {row.isSPF && (
                                            <Tooltip title="Single point of failure" arrow>
                                                <LooksOneIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                            </Tooltip>
                                        )}
                                        {row.isHub && (
                                            <Tooltip title="Structural hub (appears in ≥50% of cut sets)" arrow>
                                                <HubIcon sx={{ fontSize: 14, color: '#ed6c02' }} />
                                            </Tooltip>
                                        )}
                                        <span style={row.isBarrier ? { color: 'green', fontWeight: 600 } : undefined}>
                                            {row.name}
                                        </span>
                                    </Stack>
                                </TableCell>
                                <TableCell align="right">{row.cutSetCount}</TableCell>
                                <TableCell align="center" sx={{ backgroundColor: ratingColor(row.severity) }}>
                                    {row.severity}
                                </TableCell>
                                <TableCell align="center" sx={{ backgroundColor: ratingColor(row.occurrence) }}>
                                    {row.occurrence}
                                </TableCell>
                                <TableCell align="center" sx={{ backgroundColor: ratingColor(row.detection) }}>
                                    {row.detection}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        backgroundColor: rpnColor(row.rpn),
                                        color: rpnTextColor(row.rpn),
                                        fontWeight: 600,
                                    }}
                                >
                                    {row.rpn}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default FMEATable;
